import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { itemsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  AlertTriangle, Loader2, RefreshCw, ArrowLeft, Layers, ShieldAlert, CheckCircle2,
  Boxes, Truck, ClipboardList, ListChecks, Eye, Tag, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { MergePreviewBody } from '../components/MergePreviewBody';

const CONFIDENCE_STYLE = {
  high: 'bg-red-100 text-red-800 border-red-300',
  medium: 'bg-amber-100 text-amber-800 border-amber-300',
  low: 'bg-slate-100 text-slate-700 border-slate-300',
};

// Render an item's category as either a list of category badges or a muted
// "Uncategorized" pill. Kept tiny — used in both group review and the merge
// preview to keep the visual treatment consistent.
function CategoryRow({ categories, testid }) {
  const list = Array.isArray(categories) ? categories : [];
  if (list.length === 0) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-500"
        data-testid={testid ? `${testid}-uncategorized` : 'cat-uncategorized'}
      >
        <Tag className="h-3 w-3" />
        Uncategorized
      </span>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1" data-testid={testid}>
      {list.map((c) => (
        <span
          key={c}
          className="inline-flex items-center gap-1 rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-800"
        >
          <Tag className="h-3 w-3" />
          {c}
        </span>
      ))}
    </span>
  );
}

function ItemCard({ item, isMaster, isRecommended, onSelectMaster, disabled }) {
  const { t } = useTranslation();
  return (
    <div
      className={`relative rounded-lg border px-3 py-2.5 transition ${
        isMaster
          ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200 shadow-sm'
          : 'border-slate-200 bg-white hover:border-slate-400'
      }`}
      data-testid={`merge-item-card-${item.item_id}`}
    >
      {isMaster && (
        <div className="absolute -top-2 left-3 px-1.5 py-0.5 rounded bg-emerald-600 text-white text-[10px] font-semibold uppercase tracking-wide flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" />
          {t('duplicates.keepThisItem')}
        </div>
      )}
      {isRecommended && !isMaster && (
        <div
          className="absolute -top-2 left-3 px-1.5 py-0.5 rounded bg-sky-600 text-white text-[10px] font-semibold uppercase tracking-wide flex items-center gap-1"
          data-testid={`merge-recommended-${item.item_id}`}
        >
          <Sparkles className="h-3 w-3" />
          Recommended
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0 pt-0.5">
          <div className="font-medium text-sm text-slate-900 truncate" title={item.name}>
            {item.name}
          </div>
          <div className="mt-0.5 text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
            {item.sku && <span>SKU <span className="font-mono text-slate-700">{item.sku}</span></span>}
            {item.manufacturer_part_number && (
              <span>MPN <span className="font-mono text-slate-700">{item.manufacturer_part_number}</span></span>
            )}
            {!item.is_active && <Badge variant="outline" className="text-[10px] py-0 px-1.5">{t('status.inactive')}</Badge>}
            {item.archived_at && <Badge variant="outline" className="text-[10px] py-0 px-1.5 bg-slate-100">{t('status.archived')}</Badge>}
          </div>
          <div className="mt-1.5">
            <CategoryRow
              categories={item.category_names}
              testid={`merge-item-categories-${item.item_id}`}
            />
          </div>
        </div>
        <Button
          size="sm"
          variant={isMaster ? 'default' : 'outline'}
          onClick={onSelectMaster}
          disabled={disabled || isMaster}
          data-testid={`select-master-${item.item_id}`}
          className={`shrink-0 h-7 px-2.5 text-xs ${isMaster ? 'bg-emerald-600 hover:bg-emerald-700 pointer-events-none' : ''}`}
        >
          {isMaster ? t('duplicates.keeping') : t('duplicates.keepThis')}
        </Button>
      </div>
      <div className="mt-2 grid grid-cols-5 gap-1 text-[11px]">
        <Stat icon={<Boxes className="h-3 w-3" />} label={t('duplicates.stat.wh')} value={item.warehouse_qty} />
        <Stat icon={<Truck className="h-3 w-3" />} label={t('duplicates.stat.veh')} value={item.vehicle_qty} />
        <Stat icon={<ClipboardList className="h-3 w-3" />} label={t('duplicates.stat.po')} value={item.po_line_count} />
        <Stat icon={<ListChecks className="h-3 w-3" />} label={t('duplicates.stat.job')} value={item.job_material_count} />
        <Stat icon={<RefreshCw className="h-3 w-3" />} label={t('duplicates.stat.evt')} value={item.inventory_event_count} />
      </div>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="rounded bg-slate-50 border border-slate-200 px-1.5 py-1 flex items-center gap-1" title={`${label}: ${value || 0}`}>
      <span className="text-slate-400 shrink-0">{icon}</span>
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-slate-900 ml-auto">{value || 0}</span>
    </div>
  );
}

const REASON_KEY_MAP = {
  'Same SKU': 'duplicates.reason.sameSku',
  'Same part number': 'duplicates.reason.samePartNumber',
  'Same name (normalized)': 'duplicates.reason.sameName',
  'Similar name': 'duplicates.reason.similarName',
  'Same category + similar name': 'duplicates.reason.categoryAndName',
};

function GroupCard({ group, masterId, recommendedId, onSelectMaster, onPreview, busy }) {
  const { t } = useTranslation();
  const conf = group.confidence;
  const dupCount = Math.max(0, group.items.length - 1);
  const reasonKey = REASON_KEY_MAP[group.reason];
  const reasonText = reasonKey ? t(reasonKey) : group.reason;
  const confidenceText = t(`duplicates.confidence.${conf}`, { defaultValue: conf.toUpperCase() });
  return (
    <Card data-testid={`dup-group-${group.group_key}`} className="overflow-hidden">
      <CardHeader className="pb-3 bg-slate-50/60 border-b">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Badge variant="outline" className={`uppercase shrink-0 ${CONFIDENCE_STYLE[conf]}`}>
              {confidenceText}
            </Badge>
            <CardTitle className="text-base text-slate-900 truncate">{reasonText}</CardTitle>
            <Badge variant="outline" className="text-xs shrink-0">
              {group.items.length} {t('duplicates.items')}
            </Badge>
            <span className="text-xs text-slate-500 hidden sm:inline shrink-0">
              {t('duplicates.duplicatesWillMerge', { count: dupCount })}
            </span>
          </div>
          <Button
            size="sm"
            onClick={onPreview}
            disabled={!masterId || busy}
            data-testid={`preview-merge-${group.group_key}`}
            className="shrink-0"
          >
            <Eye className="h-4 w-4 mr-1" />
            {t('duplicates.previewMerge')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {group.items.map((item) => (
            <ItemCard
              key={item.item_id}
              item={item}
              isMaster={item.item_id === masterId}
              isRecommended={item.item_id === recommendedId}
              onSelectMaster={() => onSelectMaster(item.item_id)}
              disabled={busy}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function MergePreviewDialog({ open, onClose, preview, loading, onConfirm, busy }) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="merge-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-500" />
            {t('duplicates.previewDialog.title')}
          </DialogTitle>
          <DialogDescription>
            {t('duplicates.previewDialog.description')}
            <span className="block mt-2 font-semibold text-slate-700">
              {t('duplicates.previewDialog.noteHidden')}
            </span>
          </DialogDescription>
        </DialogHeader>

        <MergePreviewBody preview={preview} loading={loading} />

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="merge-preview-cancel">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={busy || loading || !preview}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="merge-preview-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
            {t('duplicates.previewDialog.confirmMerge')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FindDuplicates() {
  const navigate = useNavigate();
  const { isOwner } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [includeLow, setIncludeLow] = useState(false);

  // Per-group selected master_id
  const [masters, setMasters] = useState({});

  // Preview dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewGroupKey, setPreviewGroupKey] = useState(null);

  // Confirm + execute state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [merging, setMerging] = useState(false);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await itemsAPI.scanDuplicates({ include_low: includeLow });
      const fetched = resp.data?.groups || [];
      setGroups(fetched);
      // Default master selection:
      //   1) If exactly ONE item in the group has ≥1 category and the rest
      //      are uncategorized → preselect the categorized one (and mark it
      //      "Recommended" via the same item_id derivation downstream). This
      //      matches Dana's "the categorized item is usually the master"
      //      heuristic without locking the user out of choosing otherwise.
      //   2) Otherwise → keep the historical heuristic (highest combined
      //      warehouse + vehicle + po_line_count).
      const initialMasters = {};
      for (const g of fetched) {
        if (!g.items || g.items.length === 0) continue;
        const categorized = g.items.filter(
          (i) => Array.isArray(i.category_names) && i.category_names.length > 0,
        );
        if (categorized.length === 1) {
          initialMasters[g.group_key] = categorized[0].item_id;
          continue;
        }
        const sorted = [...g.items].sort((a, b) => {
          const av = (a.warehouse_qty + a.vehicle_qty + a.po_line_count);
          const bv = (b.warehouse_qty + b.vehicle_qty + b.po_line_count);
          return bv - av;
        });
        initialMasters[g.group_key] = sorted[0].item_id;
      }
      setMasters(initialMasters);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error(t('duplicates.toast.ownerOnlyAccess'));
      } else {
        toast.error(err?.response?.data?.detail || t('duplicates.toast.scanFailed'));
      }
    } finally {
      setLoading(false);
    }
  }, [includeLow, t]);

  // Per-group "Recommended" id: shown only when exactly one item is
  // categorized and the rest are uncategorized. Computed from the live
  // groups list rather than stored, so it stays in sync if the scan reruns.
  const recommendedByGroup = useMemo(() => {
    const out = {};
    for (const g of groups) {
      if (!g.items || g.items.length === 0) continue;
      const categorized = g.items.filter(
        (i) => Array.isArray(i.category_names) && i.category_names.length > 0,
      );
      if (categorized.length === 1) {
        out[g.group_key] = categorized[0].item_id;
      }
    }
    return out;
  }, [groups]);

  useEffect(() => {
    if (isOwner) fetchGroups();
  }, [fetchGroups, isOwner]);

  if (!isOwner) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-amber-500" />
                Owner-only tool
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-slate-600">
              The Find Duplicates &amp; Merge tool can only be run by the tenant Owner.
              Please ask your Owner to run a duplicate merge for you.
              <div className="mt-4">
                <Button onClick={() => navigate('/catalogue')} variant="outline">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back to Catalogue
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const handleSelectMaster = (groupKey, itemId) => {
    setMasters((prev) => ({ ...prev, [groupKey]: itemId }));
  };

  const handlePreview = async (group) => {
    const masterId = masters[group.group_key];
    if (!masterId) {
      toast.error(t('duplicates.toast.pickMaster'));
      return;
    }
    const duplicateIds = group.items
      .map((i) => i.item_id)
      .filter((id) => id !== masterId);
    if (duplicateIds.length === 0) {
      toast.error(t('duplicates.toast.noDuplicates'));
      return;
    }
    setPreviewGroupKey(group.group_key);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const resp = await itemsAPI.mergePreview({
        master_item_id: masterId,
        duplicate_item_ids: duplicateIds,
      });
      setPreviewData(resp.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('duplicates.toast.previewFailed'));
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmMerge = () => {
    // Close the preview dialog BEFORE opening the final AlertDialog. Radix
    // Dialog and AlertDialog both trap focus and stack overlays; when both
    // are open at once the AlertDialog's action button can end up behind an
    // overlay in the pointer-event stack, so clicks never reach
    // handleExecuteMerge. previewData stays in state (controlled-mode
    // open-flip does not trigger Radix's onOpenChange), so the execute
    // handler still has master + duplicates when the user confirms.
    setPreviewOpen(false);
    setConfirmOpen(true);
  };

  const handleExecuteMerge = async () => {
    if (!previewData) return;
    const masterId = previewData.master.item_id;
    const duplicateIds = (previewData.duplicates || []).map((d) => d.item_id);
    setMerging(true);
    try {
      const resp = await itemsAPI.merge({
        master_item_id: masterId,
        duplicate_item_ids: duplicateIds,
        confirm: true,
      });
      toast.success(t('duplicates.toast.mergedSuccess', { count: resp.data?.merged_count || 0 }));
      setConfirmOpen(false);
      setPreviewOpen(false);
      setPreviewData(null);
      setPreviewGroupKey(null);
      // Re-scan to refresh groups
      await fetchGroups();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('duplicates.toast.mergeFailed'));
    } finally {
      setMerging(false);
    }
  };

  const highCount = groups.filter((g) => g.confidence === 'high').length;
  const medCount = groups.filter((g) => g.confidence === 'medium').length;
  const lowCount = groups.filter((g) => g.confidence === 'low').length;

  return (
    <Layout>
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/catalogue')} data-testid="back-to-catalogue">
                <ArrowLeft className="h-4 w-4 mr-1" /> {t('duplicates.backToCatalogue')}
              </Button>
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-1 flex items-center gap-2">
              <Layers className="h-6 w-6 text-slate-500" />
              {t('duplicates.pageTitle')}
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              {t('duplicates.pageSubtitle')}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchGroups}
            disabled={loading}
            data-testid="rescan-duplicates"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {t('duplicates.rescan')}
          </Button>
        </div>

        {/* Controls */}
        <Card>
          <CardContent className="py-3 flex items-center justify-between flex-wrap gap-3" data-testid="scan-summary">
            <div className="flex items-center gap-4 text-sm" data-testid="scan-summary-counts">
              <span className="text-slate-500">{t('duplicates.found')}</span>
              <span><Badge className="bg-red-100 text-red-800 border-red-300">{highCount} {t('duplicates.confidence.high')}</Badge></span>
              <span><Badge className="bg-amber-100 text-amber-800 border-amber-300">{medCount} {t('duplicates.confidence.medium')}</Badge></span>
              {includeLow && (
                <span><Badge className="bg-slate-100 text-slate-700 border-slate-300">{lowCount} {t('duplicates.confidence.low')}</Badge></span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="include-low"
                checked={includeLow}
                onCheckedChange={setIncludeLow}
                data-testid="toggle-include-low"
              />
              <Label htmlFor="include-low" className="text-sm cursor-pointer">
                {t('duplicates.showLowConfidence')}
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Groups */}
        {loading && groups.length === 0 && (
          <div className="py-16 flex items-center justify-center text-slate-500">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            {t('duplicates.scanning')}
          </div>
        )}

        {!loading && groups.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
              <div className="text-lg font-medium text-slate-900">{t('duplicates.noGroups')}</div>
              <div className="text-sm text-slate-500 mt-1">
                {t('duplicates.noGroupsHint')}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-4">
          {groups.map((g) => (
            <GroupCard
              key={g.group_key}
              group={g}
              masterId={masters[g.group_key]}
              recommendedId={recommendedByGroup[g.group_key]}
              onSelectMaster={(itemId) => handleSelectMaster(g.group_key, itemId)}
              onPreview={() => handlePreview(g)}
              busy={merging || (previewLoading && previewGroupKey === g.group_key)}
            />
          ))}
        </div>
      </div>

      {/* Preview dialog */}
      <MergePreviewDialog
        open={previewOpen}
        onClose={() => { setPreviewOpen(false); setPreviewData(null); }}
        preview={previewData}
        loading={previewLoading}
        onConfirm={handleConfirmMerge}
        busy={merging}
      />

      {/* Final confirmation */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="merge-final-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              {t('duplicates.finalConfirm.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('duplicates.finalConfirm.description')}
              <span className="block mt-2 font-semibold text-slate-900">
                {t('duplicates.finalConfirm.warning')}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={merging} data-testid="merge-cancel-final">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleExecuteMerge}
              disabled={merging}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="merge-execute-final"
            >
              {merging ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {t('duplicates.finalConfirm.yesMergeNow')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
